import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ApiService from 'utils/services/ApiService';
import { CircularProgress, Typography } from '@mui/material';
import Button from '../button';
import Text from '../text';
import LocationsTable from '../table/LocationsTable';
import AddNewLocation from './AddNewLocation';
import { ILocations } from 'utils/models/locations';
import { IReduxState } from 'utils/models/redux-states';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { useDispatch } from 'react-redux';
import { getTestResults, saveSyncCache, saveDeletionSyncCache, saveTestResults } from 'utils/services/idbService';
import { updateExistingLocation, removeLocation, setTestKitsRedux } from 'utils/redux/reducers/updateLocationReducer';
import { setUpdatedLocations } from 'utils/redux/reducers/updateLocationReducer';

interface LocationsTestsProps {
	isOwner?: boolean;
	siteId: number;
  isOnline: boolean
}

const LocationsTests: React.FC<LocationsTestsProps> = ({ isOwner, siteId, isOnline }) => {
	const sendPopUpMessage = usePopupMessage();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const reduxLocations = useSelector((state: any) => state.updateLocation.updateLocation);
	const reduxTestKits = useSelector((state: any) => state.updateLocation.testKits);
	const userData = reduxState?.userInformation;

	const [isLoading, setIsLoading] = useState(false);
	const [showAddNewLocation, setShowAddNewLocation] = useState(false);
	const [testKits, setTestKits] = useState<any[]>([]);
	const [locations, setLocations] = useState<ILocations[]>([]);
	const dispatch = useDispatch();

	const pendingLocationIds = React.useRef<Map<number, Promise<number>>>(new Map());

	const registerPendingCreate = (tempId: number) => {
		let resolveFn!: (realId: number) => void;
		let rejectFn!: (error: any) => void;
		const promise = new Promise<number>((resolve, reject) => {
			resolveFn = resolve;
			rejectFn = reject;
		});
		pendingLocationIds.current.set(tempId, promise);
		return {
			resolve: (realId: number) => {
				resolveFn(realId);
				pendingLocationIds.current.delete(tempId);
			},
			reject: (error: any) => {
				rejectFn(error);
				pendingLocationIds.current.delete(tempId);
			},
		};
	};

	//waits for the Real ID from the server to be returned for a location that is being edited immediately
	//after creation
	const waitForRealId = async (id: number): Promise<number> => {
		const pending = pendingLocationIds.current.get(id);
		if (!pending) return id;
		return pending;
	};
	const tableHeaders = [
		{ name: 'Location', field: 'location', colSpan: 'col-span-4' },
		{ name: 'Test Kits', field: 'test Kits', colSpan: 'col-span-4' },
		{ name: '', field: 'action', colSpan: 'col-span-4' },
	];


	

  const formatTestKitsDescriptors = (testKitsToChange: any[]) => {
    return testKitsToChange?.map((testKit: any) => {
        return {
            testKitName: testKit.testKitName,  // Include the test kit name
            testKitDescription: testKit.testKitDescription || '',  // Include the description or an empty string if not present
            id: testKit.id,  // Include the test kit ID
            status: testKit.status || 'VALIDATED',  // Include the status or 'VALIDATED' if not present
            ownerOrganizationId: testKit.ownerOrganizationId || ''  // Include the owner organization ID or an empty string if not present
        };
    });
};



	const editALocation = async (data: any, action: string) => {
		let duplicateName = false;
		locations.forEach((location) => {
			if (location.name === data.name.trim() && location.id !== data.id) {
				duplicateName = true;
			}
		});
		if (duplicateName) {
			sendPopUpMessage('Error', 'Please choose a unique location name.');
			return;
		}

		//Snapshot so the table can be restored if the API call fails.
		const previousLocations = locations;

		try {
			if (action === 'edit') {
				const testKitIDs = data.testKitDescriptors.map((desc: any) => desc.id);
				const locationVO = { id: data.id, name: data.name.trim(), testKitIDs };

				await editLocationInCache(locationVO);
				setLocations((prev) =>
					prev.map((loc) =>
						loc.id === data.id
							? { ...loc, name: data.name.trim(), testKitDescriptors: data.testKitDescriptors }
							: loc
					)
				);
			} else {
				await deleteLocationInCache(data.id);
				setLocations((prev) => prev.filter((loc) => loc.id !== data.id));
			}
		} catch (error) {
			console.log(error);
			sendPopUpMessage(
				'Error',
				`Error ${action === 'edit' ? 'editing' : 'deleting'} the location.`
			);
			return;
		}

		let realId: number;
		try {
			realId = await waitForRealId(data.id);
		} catch (error) {
			console.log(error);
			setLocations(previousLocations);
			sendPopUpMessage(
				'Error',
				`Error ${action === 'edit' ? 'editing' : 'deleting'} the location.`
			);
			return;
		}

		const dataToSave = {
			siteID: siteId,
			id: realId,
			testKitDescriptorVOs: formatTestKitsDescriptors(data.testKitDescriptors),
			locationName: data.name.trim(),
			creationDate: new Date().getTime(),
			modificationDate: new Date().getTime(),
			samplePointCreator: userData?.email,
			samplePointEditor: userData?.email,
			status: action === 'edit' ? 'Active' : 'Deleted',
		};

		ApiService.editLocation(dataToSave, action)
			.then((result) => {
				if (result.status !== 200)
					throw Error('Error to connect with the server.');

				sendPopUpMessage(
					'Success',
					`Location ${data.name} was successfully ${
						action === 'edit' ? 'edited.' : 'deleted.'
					}`
				);
			})
			.catch((error) => {
				console.log(error);
				setLocations(previousLocations);
				sendPopUpMessage(
					'Error',
					`Error ${
						action === 'edit' ? 'editing' : 'deleting'
					} the location.`
				);
			});
	};

	async function editLocationInCache(locationVO: any) {
		
		let allData = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		let site = allData.find((site: {id: number}) => site.id === siteId);
		let location = site.sampleLocationAllVOs.find((location: {id: number}) => location.id === locationVO.id);

		location.name = locationVO.name;
		location.testKitIDs = locationVO.testKitIDs;

		await saveTestResults(
			`allSites`,
			allData,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

	}

	async function deleteLocationInCache(locationID: number) {
		
		let allData = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		let siteIndex = allData.findIndex((site: {id: number}) => site.id === siteId);
		let locationIndex = allData[siteIndex].sampleLocationAllVOs.findIndex((location: {id: number}) => location.id === locationID);

		allData[siteIndex].sampleLocationAllVOs.splice(locationIndex, 1);

		await saveTestResults(
			`allSites`,
			allData,
			localStorage.getItem('currentUser') ?? 'unknown'
		);
	}

	const handleEditLocation = async (locationData: any) => {
		try {
			if (!isOnline) {
				const locationUpdateData = {
					id: locationData.id,
					siteFK: siteId,
					name: locationData.name,
					testKitIDs: locationData?.testKitDescriptors?.map((tk: any) => tk.id),
					testKitNames: locationData?.testKitDescriptors?.map((tk: any) => tk.testKitName),
					customAlarmVOs: [],
					action: 'UPDATE'
				}

				// Save to sync cache
				await saveSyncCache(locationUpdateData as any, "locationSyncCache");

				// Update local cache
				await editLocationInCache(locationUpdateData);

				// Update Redux state
				dispatch(updateExistingLocation({
					...locationData,
					testKitDescriptors: formatTestKitsDescriptors(locationData?.testKitDescriptors)
				}));

				sendPopUpMessage('Success', 'Location updated successfully. Will sync when online.');
			} else {
				await editALocation(locationData, 'edit');
			}
		} catch (error) {
			console.error('Edit location error:', error);
			sendPopUpMessage('Error', 'Failed to update location');
		}
	};

	const handleDeleteLocation = async (locationData: any) => {
		try {
			const id = locationData.id ? locationData.id : locationData;
			if (!isOnline) {
				// Add to deletion sync cache
				await saveDeletionSyncCache({
					deletedLocations: [id]
				}, "deletionSyncCache");

				// Update local cache
				await deleteLocationInCache(id);

				// Update Redux state
				dispatch(removeLocation(id));

				sendPopUpMessage('Success', 'Location deleted successfully. Will sync when online.');
			} else {
				await editALocation(locationData, 'delete');
			}
		} catch (error) {
			console.error('Delete location error:', error);
			sendPopUpMessage('Error', 'Failed to delete location');
		}
	};

	const getLocations = async (noLoading?: boolean) => {
		if (!noLoading) setIsLoading(true);
		try {
			if (!isOnline) {
				const allSitesData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				const defaultTestKits = await getTestResults(`defaultTestKits`, localStorage.getItem('currentUser') ?? 'unknown');
				const SiteIndex = allSitesData?.findIndex((site: any) => site.id === siteId);
				const lc = allSitesData[SiteIndex]?.sampleLocationAllVOs.map((location: any) => {
					return {
						...location,
						testKitDescriptors: location.testKitIDs.map((testKitId: number) => {
							const defaultKit = defaultTestKits.findIndex((dKit: any)=> dKit.id ===  testKitId)
							const fieldDescriptors = defaultTestKits[defaultKit];
							return fieldDescriptors;
						}),
						dataPointAllVOs: []
					};
				});
				setLocations(lc || []);
				dispatch(setUpdatedLocations(lc || []));
			} else {
				const result = await ApiService.getLocationBySiteID(siteId);
				if (result.status !== 200) throw Error('Error connecting with the server.');
				setLocations(result.data);
				dispatch(setUpdatedLocations(result.data));
			}
		} catch (error) {
			console.error(error);
			sendPopUpMessage('Error', 'Failed to fetch locations');
		} finally {
			setIsLoading(false);
		}
	};
	const getTestKits = async () => {
		if (userData) {
			try {
				if (!isOnline) {
					const defaultTestKits = await getTestResults(`defaultTestKits`, localStorage.getItem('currentUser') ?? 'unknown');
					dispatch(setTestKitsRedux(defaultTestKits));
				} else {
					// Existing online mode code
					const result = await ApiService.getTestKits(
						userData.organizationVO.id
					);
					if (result.status !== 200)
						throw Error('Error to connect with the server.');
					dispatch(setTestKitsRedux(result.data));
				}
			} catch (error) {
				console.error('Error fetching test kits:', error);
				sendPopUpMessage('Error', 'Failed to fetch test kits');
			}
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			await getLocations();
		};
		fetchData();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			await getTestKits();
		};
		fetchData();
	}, []);

	useEffect(() => {
		setLocations(reduxLocations);
		console.log(reduxLocations, 'reduxLocations');
	}, [reduxLocations]);

	useEffect(() => {
		setTestKits(reduxTestKits);
		console.log(reduxTestKits, 'testKits');
	}, [reduxTestKits]);
	
	return (
		<div id="locations&tests">
			<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
				Sample Locations
			</Text>
			<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[24px]">
				Sample locations are areas within a site where you have collected
				samples from. <em>Example: "Inlet #1."</em><br></br>
				For each sample location, select the test kits you plan to use at it. 
			</Text>
			<div className="pt-[16px] existing-locations-inputs">
				{isLoading ? (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '200px',
						}}
					>
						<CircularProgress />
					</div>
				) : (
					<LocationsTable
						headers={tableHeaders}
						locations={locations || []}
						testKits={testKits || []}
						isOwner={isOwner}
						handleEditLocation={handleEditLocation}
						handleDeleteLocation={handleDeleteLocation}
						setLocations={setLocations}
					/>
				)}
			</div>
			<div>
				{showAddNewLocation && (
					<AddNewLocation
						headers={tableHeaders}
						setShowAddNewLocation={setShowAddNewLocation}
						locations={locations || []}
						testKits={testKits || []}
						siteId={siteId}
						userEmail={userData?.email}
						userId={userData?.id ?? 0}
						setLocations={setLocations}
						registerPendingCreate={registerPendingCreate}
					/>
				)}
			</div>
			{isOwner && (
				<div className="mt-[20px] add-new-location">
					<Button
						onClick={() => setShowAddNewLocation(true)}
						addStyle={`!h-[48px] !font-bold !text-[#0092C8] !text-[14px] !font-bold add-new-location disabled:!text-[#9dabb4]
                    `}
					>
						+ ADD SAMPLE LOCATION
					</Button>
				</div>
			)}
		</div>
	);
};

export default LocationsTests;